import React, { useState, useEffect } from "react";
import { Organization } from "../../../Entities/Organization";
import SubscriptionWarning from "../../../Components/SubscriptionWarning";
import organizationService from "../../../Service/organization.service";
import { useLoader } from "../../../Context/LoaderContext";
import { openNotification } from "../../../Components/Notification";
import type { UploadProps } from "antd";
import Dragger from "antd/es/upload/Dragger";
import { UploadOutlined, LockOutlined } from "@ant-design/icons";

interface LogDetailsProps {
  organizationDetails: Organization;
  setOrganizationDetails: any;
  limits: any;
}

const LogoDetails: React.FC<LogDetailsProps> = ({
  organizationDetails,
  setOrganizationDetails,
  limits,
}) => {
  const orgLogo = organizationDetails?.logo
    ? `https://hiring-assistant-files.s3.ap-south-1.amazonaws.com/organization/${organizationDetails?._id}/${organizationDetails?.logo}`
    : "/images/qualifyme-logo-light.png";

  const [open, setOpen] = useState<boolean>(false);
  const [isBrightLogo, setIsBrightLogo] = useState<boolean>(false);
  const { setLoading } = useLoader();

  const uploadLogo = async (file: File, url: string) => {
    const response = await fetch(url, {
      method: "PUT",
      body: file,
    });
    return response;
  };

  const saveLogo = async (filename: string) => {
    try {
      const data = await organizationService.saveLogo(filename);
      return data;
    } catch (error) {
      console.error("Error saving the logo:", error);
    }
  };

  const handleLogoUpload = async (file: File) => {
    try {
      setLoading(true);
      const uploadUrlResponse = await organizationService.getlogoUploadUrl(
        file.name,
        file.type,
      );
      const uploadUrl = uploadUrlResponse.url;
      const fileName = uploadUrlResponse.fileName;
      const uploadResponse = await uploadLogo(file, uploadUrl);
      if (uploadResponse.status === 200) {
        const data = await saveLogo(fileName);
        setOrganizationDetails(data);
        openNotification("success", "Success", "Logo uploaded successfully.");
        // Reset and re-check brightness for new logo
        setIsBrightLogo(false);
        setTimeout(() => {
          const newLogoUrl = `https://hiring-assistant-files.s3.ap-south-1.amazonaws.com/organization/${data._id}/${data.logo}`;
          checkImageBrightness(newLogoUrl);
        }, 500);
      }
    } catch (error) {
      console.error("Error uploading the file:", error);
      openNotification("error", "Error", "Failed to upload the logo.");
    } finally {
      setLoading(false);
    }
  };

  const checkImageBrightness = (imageSrc: string) => {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            setIsBrightLogo(false);
            return;
          }

          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);

          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;
          let brightness = 0;
          let pixelCount = 0;

          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];

            if (a > 0) {
              const pixelBrightness = (r + g + b) / 3;
              brightness += pixelBrightness;
              pixelCount++;
            }
          }

          if (pixelCount > 0) {
            const averageBrightness = brightness / pixelCount;
            setIsBrightLogo(averageBrightness > 200);
          } else {
            setIsBrightLogo(false);
          }
        } catch (error) {
          console.error("Error analyzing image brightness:", error);
          setIsBrightLogo(false);
        }
      };
      img.onerror = () => {
        setIsBrightLogo(false);
      };
      img.src = imageSrc;
    } catch (error) {
      console.error("Error loading image for brightness check:", error);
      setIsBrightLogo(false);
    }
  };

  useEffect(() => {
    if (orgLogo) {
      checkImageBrightness(orgLogo);
    }
  }, [orgLogo]);

  const uploadProps: UploadProps = {
    name: "file",
    multiple: false,
    accept: "image/*",
    showUploadList: false,
    beforeUpload: (file) => {
      if (!limits?.customBranding) {
        setOpen(true);
        return false;
      }
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        openNotification("error", "Error", "Only image files are allowed.");
        return false;
      }
      handleLogoUpload(file);
      return false;
    },
  };

  return (
    <div className="org-logo-section logo-details-section">
      <div
        style={{
          width: "150px",
          height: "150px",
          borderRadius: "50%",
          border: "1px solid #ddd",
          padding: "8px",
          backgroundColor: "#fff",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            backgroundColor: isBrightLogo ? "#000" : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <img
            src={orgLogo}
            alt="org-logo"
            style={{
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
        </div>
      </div>
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        <Dragger
          {...uploadProps}
          style={{ width: "100%", height: "100%" }}
          disabled={!limits?.customBranding}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-hint">
            Supported formats: JPEG, PNG. Maximum file size: 2 MB.
          </p>
        </Dragger>
        {!limits?.customBranding && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "6px",
              cursor: "pointer",
            }}
            onClick={() => setOpen(true)}
          >
            <LockOutlined
              style={{
                fontSize: "24px",
                color: "#fff",
                marginBottom: "8px",
              }}
            />
            <p
              style={{
                color: "#fff",
                fontSize: "14px",
                fontWeight: "500",
                margin: 0,
                textAlign: "center",
              }}
            >
              Upgrade plan to unlock custom branding
            </p>
          </div>
        )}
      </div>
      <SubscriptionWarning
        open={open}
        handleClose={() => setOpen(false)}
        title="Upgrade"
        type="org-logo"
      />
    </div>
  );
};

export default LogoDetails;
